import {
  Badge,
  Card,
  Divider,
  Group,
  Stack,
  Text,
} from '@mantine/core'
import {
  AlertCircle,
  Circle,
  Clock,
  MessageSquare,
  Users,
} from 'lucide-react'
import { format } from 'date-fns'

import { Ticket } from '@data/tickets/model/ticket'
import { getActionedByUsers } from '@data/tickets/ticketActivity'
import {
  IssueMap,
  priorityColors,
  statusColors,
} from '@components/main/Tickets/constants'


// Helper function to get the most recent comment
const getMostRecentComment = (ticket: Ticket) => {
  if (!ticket.comments || ticket.comments.length === 0) {
    return null
  }

  let mostRecentComment = null
  let mostRecentTimestamp = Number.NEGATIVE_INFINITY

  for (const comment of ticket.comments) {
    if (comment.deleted) continue

    const timestamp = new Date(comment.date).getTime()
    if (Number.isNaN(timestamp) || timestamp <= mostRecentTimestamp) continue

    mostRecentComment = comment
    mostRecentTimestamp = timestamp
  }

  return mostRecentComment
}

interface TicketCardProps {
  ticket: Ticket;
  onClick: () => void;
}

export const TicketCard = ({ ticket, onClick }: TicketCardProps) => {
  const actionedUsers = getActionedByUsers(ticket)
  const hasMultipleUsers = actionedUsers.length > 1
  const mostRecentComment = getMostRecentComment(ticket)

  return (
    <Card
      shadow={'sm'}
      padding={'md'}
      radius={'md'}
      withBorder
      style={{ cursor: 'pointer' }}
      onClick={onClick}
    >
      <Stack gap='sm'>
        <div>
          <Text fw={500} size={'md'} fs={'italic'}>Ticket #{(ticket.id + '').padStart(6, '0')}</Text>
          <Text fw={500} size={'sm'} mb={4}>
            {`${ticket.deviceId} - ${ticket.deviceName}`}
          </Text>
          <Text size={'xs'} c={'dimmed'} lineClamp={2}>
            {ticket.description ? ticket.description : `${IssueMap[ticket.issueType]} issue detected`}
          </Text>
        </div>
        <Divider />
        <Group gap='xs'>
          <Badge
            color={priorityColors[ticket.priority]}
            variant={'light'}
            size={'sm'}
            leftSection={
              ticket.priority === 'high' ? (
                <AlertCircle size={12} />
              ) : undefined
            }
          >
            {ticket.priority}
          </Badge>
          <Badge
            color={statusColors[ticket.status]}
            variant='light'
            size='sm'
            leftSection={<Circle size={8} fill={'currentColor'} />}
          >
            {ticket.status}
          </Badge>
        </Group>

        <Group justify={'space-between'}>
          <Group gap={4}>
            <Clock size={12} />
            <Text size={'xs'} c={'dimmed'}>
              Created
            </Text>
          </Group>
          <Text size={'xs'} c={'dimmed'}>
            {format(new Date(ticket.createdAt), 'dd-MM-yyyy HH:mm')}
          </Text>
        </Group>

        <Group justify={'space-between'}>
          <Group gap={4}>
            <Clock size={12} />
            <Text size={'xs'} c={'dimmed'}>
              Last Update
            </Text>
          </Group>
          <Text size={'xs'} c={'dimmed'}>
            {format(new Date(ticket.eventDate), 'dd-MM-yyyy HH:mm')}
          </Text>
        </Group>

        {mostRecentComment && (
          <div>
            <Group gap={4} mb={4}>
              <MessageSquare size={12} />
              <Text size={'xs'} c={'dimmed'} fw={500}>
                Recent Comment:
              </Text>
            </Group>
            <Text size={'xs'} c={'dimmed'} lineClamp={2} pl={16}>
              {mostRecentComment.comment}
            </Text>
          </div>
        )}

        <Divider />

        {hasMultipleUsers ? (
          <Group gap={4}>
            <Users size={12} />
            <Text size={'xs'} c={'dimmed'}>
              Actioned by: {actionedUsers.join(', ')}
            </Text>
          </Group>
        ) : ticket.userName ? (
          <Group gap={4}>
            <Users size={12} />
            <Text size={'xs'} c={'dimmed'}>
              {/*Assigned to: {UserNames[ticket.userIdRef]}*/}
              Assigned to: {ticket.userName}
            </Text>
          </Group>
        ) : null}
      </Stack>
    </Card>
  )
}
